// Utility for triggering AI chat from anywhere in the app
// This creates a communication bridge to the GlobalDashboardAI component

export interface AIChatTriggerEvent {
  type: 'trigger_ai_chat';
  prompt: string;
  context?: any;
  source?: string;
}

class AIChatTrigger {
  private static instance: AIChatTrigger;
  
  private constructor() {}
  
  static getInstance(): AIChatTrigger {
    if (!AIChatTrigger.instance) {
      AIChatTrigger.instance = new AIChatTrigger();
    }
    return AIChatTrigger.instance;
  }
  
  // Trigger AI chat with specific prompt
  triggerChat(prompt: string, context?: any, source?: string) {
    const event = new CustomEvent('ai-chat-trigger', {
      detail: {
        type: 'trigger_ai_chat',
        prompt,
        context,
        source
      } as AIChatTriggerEvent
    });
    
    window.dispatchEvent(event);
    console.log(`🤖 [AI TRIGGER] Triggering chat from ${source}:`, prompt);
  }
  
  // Pre-defined prompts for dashboard actions
  triggerDetailedAnalysis(leads: any[], deals: any[], analytics: any) {
    const analysisPrompt = `Generate a comprehensive analysis of my current dashboard metrics:

📊 **Current Performance:**
- ${leads?.length || 0} active leads
- ${deals?.length || 0} active deals  
- ${analytics.conversionRate} conversion rate
- ${analytics.hotLeads} hot prospects identified
- ${analytics.avgResponseTime} average response time

Please provide:
1. **Performance Assessment**: How am I performing compared to industry standards?
2. **Key Opportunities**: What are the top 3 areas for immediate improvement?
3. **Action Plan**: Specific steps I should take this week
4. **ROI Optimization**: How can I maximize return on my current pipeline?

Make recommendations specific to my actual data, not generic advice.`;

    this.triggerChat(analysisPrompt, { leads, deals, analytics }, 'detailed_analysis');
  }
  
  triggerAutoActionsSetup(leads: any[], deals: any[], analytics: any) {
    const autoActionsPrompt = `Help me set up automated actions based on my current business data:

🤖 **Current Situation:**
- Lead volume: ${leads?.length || 0} active leads
- Hot leads: ${analytics.hotLeads} requiring immediate attention
- Response time: ${analytics.avgResponseTime}
- Conversion rate: ${analytics.conversionRate}

**Setup Request:**
1. **Lead Automation**: What automated follow-ups should I enable for my ${leads?.length || 0} leads?
2. **Hot Lead Actions**: How should I automatically prioritize my ${analytics.hotLeads} hot prospects?
3. **Response Optimization**: What automation can improve my ${analytics.avgResponseTime} response time?
4. **Conversion Workflows**: What automated sequences would boost my ${analytics.conversionRate} conversion rate?

Please recommend specific automation workflows I can implement immediately, prioritized by potential impact.`;

    this.triggerChat(autoActionsPrompt, { leads, deals, analytics }, 'auto_actions_setup');
  }
  
  triggerBudgetOptimization(leads: any[], deals: any[], analytics: any, totalPipelineValue: number) {
    const budgetPrompt = `Analyze my marketing budget allocation and ROI optimization:

💰 **Current Investment Data:**
- Pipeline value: $${totalPipelineValue.toLocaleString()}
- Conversion rate: ${analytics.conversionRate}
- Lead volume: ${leads?.length || 0} active leads
- Deal progression: ${deals?.length || 0} active deals

**Budget Analysis Request:**
1. **ROI Assessment**: Is my current ${analytics.conversionRate} conversion rate providing good ROI?
2. **Lead Source Optimization**: Where should I invest more/less based on performance?
3. **Budget Reallocation**: How should I redistribute my marketing spend?
4. **Scaling Strategy**: If I wanted to double my leads, where should I invest?

**Current Challenges:**
- ${leads?.length || 0 < 10 ? 'Low lead volume' : 'Managing lead volume'}
- ${analytics.hotLeads < 3 ? 'Few hot prospects' : 'Hot prospect prioritization'}
- Response time optimization

Please provide specific budget recommendations with expected ROI for each suggestion.`;

    this.triggerChat(budgetPrompt, { leads, deals, analytics, totalPipelineValue }, 'budget_optimization');
  }

  // AI Insights Panel Triggers
  triggerMarketAnalysis(leads: any[], deals: any[], analytics: any, totalPipelineValue: number) {
    const marketPrompt = `Provide a comprehensive market analysis and hot opportunities:

🏠 **Current Market Position:**
- Active portfolio: ${leads?.length || 0} leads, ${deals?.length || 0} deals
- Pipeline value: $${totalPipelineValue.toLocaleString()}
- Conversion performance: ${analytics.conversionRate}
- Hot prospects: ${analytics.hotLeads} ready for action

**Market Analysis Request:**
1. **Market Trends**: What are the current real estate market trends I should know about?
2. **Hot Opportunities**: Based on my ${analytics.hotLeads} hot leads, what immediate opportunities should I prioritize?
3. **Competitive Analysis**: How can I position myself better in this market?
4. **Seasonal Strategy**: What market patterns should I expect in the coming months?
5. **Geographic Focus**: Which areas or neighborhoods should I target based on current trends?

**Specific Questions:**
- Should I adjust my pricing strategy for current market conditions?
- What marketing channels are most effective in today's market?
- How can I leverage my ${totalPipelineValue > 500000 ? 'high-value' : 'growing'} pipeline for market advantage?

Please provide actionable market insights and specific recommendations for my business.`;

    this.triggerChat(marketPrompt, { leads, deals, analytics, totalPipelineValue }, 'market_analysis');
  }

  triggerOpportunityDetection(leads: any[], deals: any[], analytics: any) {
    const opportunityPrompt = `Analyze my business data and detect immediate opportunities:

🎯 **Opportunity Detection Analysis:**
- Current leads: ${leads?.length || 0} in pipeline
- Active deals: ${deals?.length || 0} in progress
- Hot prospects: ${analytics.hotLeads} requiring immediate attention
- Conversion rate: ${analytics.conversionRate}
- Response time: ${analytics.avgResponseTime}

**Opportunity Analysis Request:**
1. **Immediate Opportunities**: What opportunities am I missing with my current ${leads?.length || 0} leads?
2. **Hot Lead Prioritization**: How should I prioritize my ${analytics.hotLeads} hot prospects for maximum conversion?
3. **Deal Acceleration**: Which of my ${deals?.length || 0} active deals can be accelerated to close faster?
4. **Lead Nurturing**: What leads in my pipeline have the highest potential but are being underutilized?
5. **Timing Opportunities**: Are there any seasonal or market timing advantages I should leverage now?

**Specific Detection Areas:**
- Leads that haven't been contacted recently but show high potential
- Deals that are stalling and need intervention
- Market segments I'm not fully exploiting
- Referral opportunities from existing clients
- Cross-selling and upselling possibilities

Please identify specific, actionable opportunities I can pursue immediately to grow my business.`;

    this.triggerChat(opportunityPrompt, { leads, deals, analytics }, 'opportunity_detection');
  }

  triggerSystemIntegration(leads: any[], deals: any[], analytics: any) {
    const integrationPrompt = `Help me optimize my system integrations and workflow efficiency:

⚙️ **Current System Status:**
- Lead management: ${leads?.length || 0} active leads
- Deal tracking: ${deals?.length || 0} active deals
- Response time: ${analytics.avgResponseTime}
- SMS campaigns: ${analytics.activeSmsCampaigns} active
- Conversion rate: ${analytics.conversionRate}

**Integration Optimization Request:**
1. **Twilio SMS Integration**: How can I better utilize SMS automation for my ${leads?.length || 0} leads?
2. **CRM Workflow**: What workflow improvements would boost my ${analytics.conversionRate} conversion rate?
3. **Communication Sync**: How can I improve my ${analytics.avgResponseTime} response time through better integrations?
4. **Lead Routing**: What's the best way to automatically route and prioritize my leads?
5. **Data Flow**: How can I ensure better data synchronization across all my tools?

**System Enhancement Areas:**
- Automated follow-up sequences based on lead behavior
- Integration between SMS, email, and CRM data
- Real-time notifications for hot leads and urgent deals
- Performance tracking and analytics dashboards
- Mobile app integration for on-the-go management

**Current Challenges:**
- ${analytics.activeSmsCampaigns === 0 ? 'No active SMS campaigns - need automation setup' : 'SMS campaign optimization needed'}
- ${analytics.avgResponseTime.includes('hour') ? 'Response time could be faster with automation' : 'Response time is good but could be automated'}

Please provide specific recommendations for optimizing my system integrations and workflow automation.`;

    this.triggerChat(integrationPrompt, { leads, deals, analytics }, 'system_integration');
  }
}

export const aiChatTrigger = AIChatTrigger.getInstance(); 